import { flattenToc, parseCfi } from '@mhe/reader/utils';
import { BonsaiTree, TagLocation, TagNode, TagType, TocItem } from '@mhe/reader/models';
import { TopicResolutionHints } from '../topics-metadata.resolver';

export type TopicTocMatchType = 'anchor' | 'spine-title';

export interface TopicTocCandidate {
  tocId: string | null
  tocLabel: string
  spinePos: number
  spineItemId: string
  matchType: TopicTocMatchType
  score: number
}

export interface TopicTocReverseMapEntry {
  subjectId?: string
  subjectTitle?: string
  categoryId?: string
  categoryTitle?: string
  topicId: string
  topicTitle: string
  location: TagLocation
  parsedCfi: {
    spinePos: number
    spineId: string | null
    elementId?: string | null
  }
  bestCandidate?: TopicTocCandidate
  candidates: TopicTocCandidate[]
}

export interface TopicTocReverseMap {
  entries: TopicTocReverseMapEntry[]
  byAnchorUuid: Record<string, TopicTocReverseMapEntry[]>
  bySpineId: Record<string, TopicTocReverseMapEntry[]>
  bySpinePos: Record<number, TopicTocReverseMapEntry[]>
  byTocId: Record<string, TopicTocReverseMapEntry[]>
}

export const EMPTY_TOPIC_TOC_REVERSE_MAP: TopicTocReverseMap = {
  entries: [],
  byAnchorUuid: {},
  bySpineId: {},
  bySpinePos: {},
  byTocId: {},
};

export function buildTopicTocReverseMap(
  topicsTree: BonsaiTree<TagNode | Record<string, unknown>>,
  toc: TocItem[],
): TopicTocReverseMap {
  const flatToc = flattenToc(toc || []);
  const entries = collectTopicEntries(topicsTree, flatToc);

  return {
    entries,
    byAnchorUuid: indexEntries(entries, (entry) => entry.location.anchorUuid),
    bySpineId: indexEntries(entries, (entry) => entry.location.spineId),
    bySpinePos: indexEntries(entries, (entry) => entry.parsedCfi.spinePos),
    byTocId: indexEntries(entries, (entry) => entry.bestCandidate?.tocId || undefined),
  };
}

export function findTopicTocReverseMapEntry(
  reverseMap: TopicTocReverseMap | undefined,
  selectedLocation: TagLocation | null,
  _currentSpineId: string,
  hints?: TopicResolutionHints,
): TopicTocReverseMapEntry | null {
  if (!reverseMap?.entries?.length) {
    return null;
  }

  if (selectedLocation?.cfi) {
    const exactCfi = reverseMap.entries.find((entry) => entry.location.cfi === selectedLocation.cfi);
    if (exactCfi) {
      return exactCfi;
    }
  }

  if (selectedLocation?.anchorUuid) {
    const bySelectedAnchor = pickBestEntry(
      reverseMap.byAnchorUuid[selectedLocation.anchorUuid],
      selectedLocation.title,
    );
    if (bySelectedAnchor) {
      return bySelectedAnchor;
    }
  }

  if (hints?.targetAnchorUuid) {
    const byAnchor = pickBestEntry(reverseMap.byAnchorUuid[hints.targetAnchorUuid], hints.targetTitle);
    if (byAnchor) {
      return byAnchor;
    }

    const byTocId = pickBestEntry(reverseMap.byTocId[hints.targetAnchorUuid], hints.targetTitle);
    if (byTocId) {
      return byTocId;
    }
  }

  if (hints?.targetCfi) {
    const byTargetCfi = reverseMap.entries.find((entry) => entry.location.cfi === hints.targetCfi);
    if (byTargetCfi) {
      return byTargetCfi;
    }
  }

  return null;
}

export function summarizeTopicTocReverseMap(reverseMap: TopicTocReverseMap): Record<string, number> {
  return reverseMap.entries.reduce<Record<string, number>>((summary, entry) => {
    const matchType = entry.bestCandidate?.matchType || 'unmatched';
    summary[matchType] = (summary[matchType] || 0) + 1;
    return summary;
  }, {});
}

function collectTopicEntries(
  topicsTree: BonsaiTree<TagNode | Record<string, unknown>>,
  flatToc: TocItem[],
): TopicTocReverseMapEntry[] {
  const nodes = Object.values(topicsTree).filter(
    (node): node is TagNode => node && typeof node === 'object' && 'locations' in node,
  );

  return nodes.flatMap((topicNode) => {
    if (!topicNode.locations?.length) {
      return [];
    }

    const categoryNode = findNearestCategoryNode(topicsTree, topicNode);
    const subjectNode = categoryNode ? findParentSubjectNode(topicsTree, categoryNode) : undefined;

    return topicNode.locations.map((location) => {
      const parsed = parseCfi(location.cfi);
      const candidates = buildCandidates(flatToc, location, topicNode, categoryNode, parsed.spinePos);

      return {
        subjectId: subjectNode?.id,
        subjectTitle: subjectNode?.title || subjectNode?.name,
        categoryId: categoryNode?.id,
        categoryTitle: categoryNode?.title || categoryNode?.name,
        topicId: topicNode.id,
        topicTitle: topicNode.title || topicNode.name,
        location,
        parsedCfi: {
          spinePos: parsed.spinePos,
          spineId: parsed.spineId ?? null,
          elementId: parsed.steps?.find((step) => Boolean(step.id))?.id || null,
        },
        bestCandidate: candidates[0],
        candidates,
      };
    });
  });
}

function buildCandidates(
  flatToc: TocItem[],
  location: TagLocation,
  topicNode: TagNode,
  categoryNode: TagNode | undefined,
  locationSpinePos: number,
): TopicTocCandidate[] {
  const candidateMap = new Map<string, TopicTocCandidate>();
  const normalizedTopicTitle = normalizeText(topicNode.title || topicNode.name || location.title);
  const normalizedCategoryTitle = normalizeText(categoryNode?.title || categoryNode?.name || '');
  const normalizedLocationTitle = normalizeText(location.title || '');

  for (const tocItem of flatToc) {
    const tocId = tocItem.id;
    const tocLabel = tocItem.label || '';
    const normalizedTocLabel = normalizeText(tocLabel);
    const sameSpineId = tocItem.spineItem?.id === location.spineId;
    const sameSpinePos = tocItem.spinePos === locationSpinePos;

    if (tocId && tocId === location.anchorUuid) {
      upsertCandidate(candidateMap, tocItem, 'anchor', 100);
      continue;
    }

    if ((sameSpineId || sameSpinePos) && (
      titlesRoughlyMatch(normalizedTocLabel, normalizedLocationTitle) ||
      titlesRoughlyMatch(normalizedTocLabel, normalizedTopicTitle) ||
      titlesRoughlyMatch(normalizedTocLabel, normalizedCategoryTitle)
    )) {
      upsertCandidate(candidateMap, tocItem, 'spine-title', 80);
      continue;
    }
  }

  return [...candidateMap.values()].sort((left, right) => right.score - left.score);
}

function upsertCandidate(
  candidateMap: Map<string, TopicTocCandidate>,
  tocItem: TocItem,
  matchType: TopicTocMatchType,
  score: number,
): void {
  const candidateKey = `${tocItem.id || 'null'}:${tocItem.spinePos}`;
  const existing = candidateMap.get(candidateKey);

  if (!existing || score > existing.score) {
    candidateMap.set(candidateKey, {
      tocId: tocItem.id,
      tocLabel: tocItem.label,
      spinePos: tocItem.spinePos,
      spineItemId: tocItem.spineItem?.id,
      matchType,
      score,
    });
  }
}

function findNearestCategoryNode(
  topicsTree: BonsaiTree<TagNode | Record<string, unknown>>,
  node: TagNode,
): TagNode | undefined {
  let current: TagNode | undefined = node;

  while (current?.parent) {
    const parent = topicsTree[current.parent] as TagNode | undefined;
    if (!parent) {
      return undefined;
    }

    if (parent.type === TagType.SUBJECT) {
      return current;
    }

    current = parent;
  }

  return undefined;
}

function findParentSubjectNode(
  topicsTree: BonsaiTree<TagNode | Record<string, unknown>>,
  node: TagNode,
): TagNode | undefined {
  let current: TagNode | undefined = node;

  while (current?.parent) {
    const parent = topicsTree[current.parent] as TagNode | undefined;
    if (!parent) {
      return undefined;
    }

    if (parent.type === TagType.SUBJECT) {
      return parent;
    }

    current = parent;
  }

  return undefined;
}

function indexEntries<T extends string | number>(
  entries: TopicTocReverseMapEntry[],
  getKey: (entry: TopicTocReverseMapEntry) => T | undefined,
): Record<T, TopicTocReverseMapEntry[]> {
  const indexedEntries: Record<T, TopicTocReverseMapEntry[]> = Object.create(null);

  entries.forEach((entry) => {
    const key = getKey(entry);
    if (key === undefined || key === null || key === '') {
      return;
    }

    const bucket = indexedEntries[key] || [];
    indexedEntries[key] = [...bucket, entry];
  });

  return indexedEntries;
}

function pickBestEntry(
  entries: TopicTocReverseMapEntry[] | undefined,
  titleHint?: string,
): TopicTocReverseMapEntry | null {
  if (!entries?.length) {
    return null;
  }

  if (!titleHint) {
    return entries[0];
  }

  const normalizedTitleHint = normalizeText(titleHint);
  return entries.find((entry) => entryMatchesTitle(entry, normalizedTitleHint)) || entries[0];
}

function entryMatchesTitle(entry: TopicTocReverseMapEntry, normalizedTitleHint: string): boolean {
  return [
    entry.topicTitle,
    entry.categoryTitle,
    entry.subjectTitle,
    entry.location.title,
    entry.bestCandidate?.tocLabel,
  ].some((value) => titlesRoughlyMatch(normalizeText(value || ''), normalizedTitleHint));
}

function normalizeText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ');
}

function titlesRoughlyMatch(left: string, right: string): boolean {
  if (!left || !right) {
    return false;
  }

  return left === right || left.includes(right) || right.includes(left);
}
